import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import log from 'loglevel';
import { useSearchParams } from 'react-router-dom';
import useNavigate from '../../../hooks/useNavigate';
import { PencilAlt } from '../../../icons/pencil-alt';
import { ActionDelete } from '../../../icons/action-delete';
import { Briefcase } from '../../../icons/briefcase';
import { ConnectAsUser } from '../../../icons/ConnectAsUser';
import { STRING_KEYS } from '../../../language/keys';
import MenuMoreVert from '../../../components/MenuMoreVert';
import {  TABLE } from '../../../consts';
import AccountDeleteDialog from '../../../components/Dialogs/AccountDeleteDialog';
import { PERMISSIONS } from '../../../consts/permissions';
import { useAuth } from '../../../hooks/useAuth';

const TAG = '[ACCOUNT_ACTION_COMPONENT]';
const { ACTIONS } = TABLE;
const EXTRA_ACTIONS = {
  VIEW_POSITIONS: 'VIEW_POSITIONS',
  LOGIN_AS_ADMIN: 'LOGIN_AS_ADMIN',
};
const ACTION_OPTIONS = {
  ...ACTIONS,
  ...EXTRA_ACTIONS,
};

type AccountAction = {
  accountId: string,
  accountName: string,
  onDeleteHandler: (accountId: string) => void,
  onEditAccountClick: () => void,
  onLoginAsAdminClick: () => void,
}

export const AccountsActions = ({
  accountId,
  accountName,
  onDeleteHandler,
  onEditAccountClick,
  onLoginAsAdminClick,
}: AccountAction): JSX.Element | null => {
  const [isDeleteDialogVisible, setDeleteDialogVisibility] = useState(false);
  const { t: translate } = useTranslation();
  const { goToPositionsFilteredByAccount, goToAccountView } = useNavigate();
  const { hasPermissions } = useAuth();
  const [searchParams] = useSearchParams();

  const prepareAccountViewOption = () => {
    if (hasPermissions([PERMISSIONS.ACCOUNT_EDIT])) {
      return {
        type: ACTION_OPTIONS.EDIT,
        title: translate(STRING_KEYS.ACCOUNTS_ACTION_MENU_EDIT),
        icon: <PencilAlt />,
      };
    }

    return {
      type: ACTION_OPTIONS.VIEW,
      title: translate(STRING_KEYS.VIEW),
      icon: <PencilAlt />,
      permissions: [PERMISSIONS.ACCOUNT_VIEW],
    };
  };

  const actionOptions = [
    {
      ...prepareAccountViewOption(),
    },
    {
      type: ACTION_OPTIONS.VIEW_POSITIONS,
      title: translate(STRING_KEYS.ACCOUNTS_ACTION_MENU_VIEW_POSITIONS),
      icon: <Briefcase />,
      permissions: [PERMISSIONS.POSITION_VIEW],
    },
    {
      type: ACTION_OPTIONS.LOGIN_AS_ADMIN,
      title: translate(STRING_KEYS.ACCOUNTS_ACTION_MENU_LOGIN_AS_ADMIN),
      icon: <ConnectAsUser />,
      permissions: [PERMISSIONS.ACCOUNT_LOGIN_AS_ADMIN],
    },
    {
      type: ACTION_OPTIONS.DELETE,
      title: translate(STRING_KEYS.ACCOUNTS_ACTION_MENU_DELETE),
      icon: <ActionDelete />,
      permissions: [PERMISSIONS.ACCOUNT_DELETE],
    },
  ];

  const actions = actionOptions.filter((action) => {
    return !action.permissions || hasPermissions(action.permissions);
  }).map((action) => ({
    ...action,
    key: accountId,
  }));

  const handleDeleteClicked = () => {
    setDeleteDialogVisibility(true);
  };

  const handleCloseDeleteDialogClicked = () => {
    setDeleteDialogVisibility(false);
  };

  const handleDeleteConfirmed = () => {
    setDeleteDialogVisibility(false);
    onDeleteHandler(accountId);
  };

  const handleActions = (key: string, type: string) => {
    switch (type) {
      case ACTION_OPTIONS.VIEW:
        goToAccountView(key, searchParams.toString(), btoa(searchParams.toString()));
        break;
      case ACTION_OPTIONS.EDIT:
        onEditAccountClick();
        break;
      case ACTION_OPTIONS.VIEW_POSITIONS:
        goToPositionsFilteredByAccount(key);
        break;
      case ACTION_OPTIONS.LOGIN_AS_ADMIN:
        onLoginAsAdminClick();
        break;
      case ACTIONS.DELETE:
        handleDeleteClicked();
        break;
      default:
        log.warn(TAG, `Cannot find any handler for type:${type}`);
    }
  };

  if (!actions || actions.length <= 0) {
    return null;
  }

  return (
    <>
      <MenuMoreVert actions={ actions } handleActions={ handleActions } />
      <AccountDeleteDialog
        accountName={ accountName || '' }
        isOpen={ isDeleteDialogVisible }
        onConfirm={ handleDeleteConfirmed }
        onClose={ handleCloseDeleteDialogClicked }
      />
    </>
  );
};
